import React, { useState, useMemo, useCallback } from "react"
import { useMutation } from "react-query"
import { useHistory } from "react-router"
import { Container, Grid, Chip } from "@material-ui/core"
import TableContainerServerSide from "../components/TableContainerServerSide"
import DefaultFilter from "../components/DefaultFilter"
import SelectColumnFilter from "../components/SelectFilter"
import { fetchDeals, deleteDeal } from "../services/deal"
import moment from "moment"

const paymentStatusChip = {
  paid:     "chip-green",
  partial:  "chip-orange",
  awaiting: "chip-red",
}

function Deals() {
  const history = useHistory()
  const [deals, setDeals] = useState([])
  const [total, setTotal] = useState(0)
  const [pageCount, setPageCount] = useState(1)

  const { mutate: fetch, isLoading } = useMutation(
    ({ pageIndex, pageSize, search = {} }) =>
      fetchDeals({ page: pageIndex + 1, limit: pageSize, ...search }),
    {
      onSuccess: (res, { pageSize }) => {
        setDeals(res.deals || [])
        setTotal(res.total || 0)
        setPageCount(Math.ceil((res.total || 0) / pageSize) || 1)
      },
    }
  )

  const { mutate: remove, isLoading: isDeleting } = useMutation(deleteDeal, {
    onSuccess: () => {
      alert("Deal deleted")
      fetch({ pageIndex: 0, pageSize: 20 })
    },
    onError: err => alert(err?.data?.message || "Failed to delete deal"),
  })

  const columns = useMemo(() => [
    {
      accessor: "name",
      Header: "Name",
      Filter: DefaultFilter,
    },
    {
      accessor: "type",
      Header: "Type",
      Filter: SelectColumnFilter,
      FilterOptions: ["new", "renewal"],
      Cell: ({ value }) => (
        <span style={{ textTransform: "capitalize" }}>{value}</span>
      ),
    },
    {
      accessor: "country_name",
      Header: "Country",
      Filter: DefaultFilter,
    },
    {
      accessor: "sales",
      Header: "Sales",
      disableFilters: true,
      Cell: ({ row }) => {
        const { sales, currency_symbol } = row.original
        return <span>{currency_symbol}{Number(sales).toFixed(2)}</span>
      },
    },
    {
      accessor: "cash_collected",
      Header: "Cash Collected",
      disableFilters: true,
      Cell: ({ row }) => {
        const { cash_collected, currency_symbol } = row.original
        return <span>{currency_symbol}{Number(cash_collected).toFixed(2)}</span>
      },
    },
    {
      accessor: "payment_status",
      Header: "Payment Status",
      disableFilters: true,
      Cell: ({ value }) => (
        <Chip
          size="small"
          className={paymentStatusChip[value] || "chip-gray"}
          label={value}
        />
      ),
    },
    {
      accessor: "campaign_count",
      Header: "Campaigns",
      disableFilters: true,
    },
    {
      accessor: "date_add",
      Header: "Created",
      disableFilters: true,
      Cell: ({ value }) => moment(value).format("ll"),
    },
    {
      id: "actions",
      Header: "Actions",
      disableFilters: true,
      Cell: ({ row }) => {
        const { id } = row.original
        return (
          <div className="is-flex aic">
            <a className="stllr-btn btn-sm primary bordered" href={`/deals/${id}`}>View</a>
            <button
              className="stllr-btn btn-sm primary bordered ml-10"
              onClick={() => history.push(`/deals/${id}/edit`)}
            >
              Edit
            </button>
            <button
              className="stllr-btn btn-sm danger ml-10"
              disabled={isDeleting}
              onClick={() => {
                if (window.confirm("Delete this deal? All campaigns will be unlinked.")) {
                  remove(id)
                }
              }}
            >
              Delete
            </button>
          </div>
        )
      },
    },
  ], [isDeleting])

  const handleRefetchData = useCallback(({ pageIndex, pageSize, search }) => {
    fetch({ pageIndex, pageSize, search })
  }, [])

  return (
    <Container spacing={1} maxWidth="xl">
      <Grid item lg={12}>
        <div className="is-flex aic jcsb">
          <h2 className="mt-0 mb-5 mt-20">Deals (<i>{total}</i>)</h2>
          <a className="stllr-btn primary btn-md" href="/deals/new">
            Create deal
          </a>
        </div>

        <TableContainerServerSide
          name="deals"
          paging={true}
          columns={columns}
          hiddenColumns={[]}
          data={deals}
          hasFilters={true}
          loading={isLoading}
          pageCount={pageCount}
          fetchData={handleRefetchData}
        />
      </Grid>
    </Container>
  )
}

export default Deals
